import {
  Button,
  Checkbox,
  Combobox,
  Drawer,
  Field,
  Grid,
  HStack,
  Input,
  Menu,
  Portal,
  Separator,
  Stack,
  Select,
  createListCollection,
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getAllBanks } from '@/api/settings.api'
import { onSessionReset } from '@/api/sessionScope'
import { UNION_BANK_ID } from '@/common/unionBank.common'
import { TOPIC_OPTIONS, type CreateDemandPayload, type Demand } from '../types'
import { getBankDetails } from '../api'
import { SALES_REGION_OPTIONS } from '@/common/salesRegion'

type BucketInput = {
  toj: string
  consulting: string
  training: string
  pilot: string
}

type BankItem = {
  id: number
  name: string
  active?: boolean
  blzNum?: string | null
}

interface CreateDemandDrawerProps {
  open: boolean
  isSubmitting?: boolean
  demand?: Demand | null
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: CreateDemandPayload) => void
}

const salesRegionCollection = createListCollection({
  items: SALES_REGION_OPTIONS,
})

// Session-level cache so the paginated bank request runs only once,
// not on every drawer open. Session-level literally: it is dropped on any
// authentication transition (SIB-924).
let unionBanksPromise: Promise<BankItem[]> | null = null

onSessionReset(() => {
  unionBanksPromise = null
})

function loadUnionBanks() {
  if (!unionBanksPromise) {
    const p = (async () => {
      const childBanks = await getAllBanks({ parentId: UNION_BANK_ID, status: 'ACTIVE' })

      return childBanks
        .filter(bank => bank.active !== false)
        .map(bank => ({ id: bank.id, name: bank.name, active: bank.active, blzNum: bank.blzNum }))
    })()

    unionBanksPromise = p
    p.catch(() => {
      // Only clear our own failed promise: after a session reset this slot may
      // already hold the next session's load.
      if (unionBanksPromise === p) {
        unionBanksPromise = null
      }
    })
  }

  return unionBanksPromise
}

function getBucketValue(demand: Demand | null, serviceType: string) {
  return String(
    demand?.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? ''
  )
}

export function CreateDemandDrawer({
  open,
  isSubmitting = false,
  demand = null,
  onOpenChange,
  onSubmit,
}: CreateDemandDrawerProps) {
  const [customerId, setCustomerId] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [banks, setBanks] = useState<BankItem[]>([])
  const [isLoadingBanks, setIsLoadingBanks] = useState(false)
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [version, setVersion] = useState('')
  const [topics, setTopics] = useState<string[]>([])

  const bankInputRef = useRef<HTMLInputElement | null>(null)

  const [buckets, setBuckets] = useState<BucketInput>({
    toj: '',
    consulting: '',
    training: '',
    pilot: '',
  })

  const bankCollection = useMemo(() => {
    const query = customerSearch.trim().toLowerCase()

    // Match on name or bank code, like the bank management search (SIB-914).
    const filteredBanks = query
      ? banks.filter(
          bank =>
            bank.name.toLowerCase().includes(query) || String(bank.blzNum ?? '').includes(query)
        )
      : banks

    return createListCollection({
      items: filteredBanks.map(bank => ({ label: bank.name, value: String(bank.id) })),
    })
  }, [banks, customerSearch])

  const selectedBank = banks.find(bank => String(bank.id) === customerId)

  useEffect(() => {
    if (!open || !selectedBank) return

    setCustomerSearch(selectedBank.name)
  }, [open, selectedBank])

  const canSubmit = Boolean(
    customerId.trim() &&
      postalCode.trim() &&
      city.trim() &&
      version.trim() &&
      periodStart &&
      periodEnd
  )

  // Prefetch on mount (the drawer is mounted with the page), so the list is
  // usually ready before the drawer is opened.
  useEffect(() => {
    let cancelled = false

    setIsLoadingBanks(true)

    loadUnionBanks()
      .then(items => {
        if (!cancelled) setBanks(items)
      })
      .catch(() => {
        if (!cancelled) setBanks([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBanks(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!open) return

    setCustomerId(demand?.customerId ? String(demand.customerId) : '')
    setCustomerSearch('')

    setPostalCode(demand?.postal ?? demand?.postal ?? '')
    setCity(demand?.city ?? '')
    setVersion(demand?.salesRegion ?? '')
    setPeriodStart(demand?.periodStart ?? '')
    setPeriodEnd(demand?.periodEnd ?? '')
    setTopics(demand?.topic ? demand.topic.split(',').map(value => value.trim()) : [])

    setBuckets({
      toj: getBucketValue(demand, 'TOJ'),
      consulting: getBucketValue(demand, 'CONSULTING'),
      training: getBucketValue(demand, 'TRAINING'),
      pilot: getBucketValue(demand, 'PILOT'),
    })
  }, [open, demand])

  useEffect(() => {
    async function loadSelectedBankDetails() {
      if (!customerId) return

      const bank = await getBankDetails(customerId)

      setPostalCode(bank.postal ?? '')
      setCity(bank.city ?? '')
      setVersion(bank.salesRegion ?? '')
    }

    void loadSelectedBankDetails()
  }, [customerId])

  function updateBucket(field: keyof BucketInput, value: string) {
    setBuckets(current => ({
      ...current,
      [field]: value,
    }))
  }

  function bucketItems() {
    return [
      { serviceType: 'TOJ', requestedUnits: Number(buckets.toj || 0) },
      { serviceType: 'CONSULTING', requestedUnits: Number(buckets.consulting || 0) },
      { serviceType: 'TRAINING', requestedUnits: Number(buckets.training || 0) },
      { serviceType: 'PILOT', requestedUnits: Number(buckets.pilot || 0) },
    ].filter(bucket => bucket.requestedUnits > 0)
  }

  function handleSubmit() {
    if (!canSubmit) return

    onSubmit({
      bankId: 1,
      customerId: Number(customerId),
      visibilityMode: 'ALL_USERS',
      selectedUserIds: [],

      periodStart,
      periodEnd,

      postal: postalCode || undefined,
      city: city || undefined,
      country: 'DE',
      salesRegion: version || undefined,

      topic: topics.join(',') || undefined,
      description: undefined,

      buckets: ['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'].map(serviceType => ({
        serviceType,
        requestedUnits:
          bucketItems().find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0,
      })),

      periodRangeValid: true,
      visibilitySelectionValid: true,
    })
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={details => onOpenChange(details.open)}
      size="xl"
      initialFocusEl={() => bankInputRef.current}
    >
      <Portal>
        <Drawer.Backdrop />

        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <Stack gap={1}>
                <Drawer.Title>
                  {demand ? 'Bedarf bearbeiten' : 'Neuen Bedarf erfassen'}
                </Drawer.Title>
                <Drawer.Description>
                  {demand ? 'Bedarf aktualisieren.' : 'Bedarf prüfen statt direkt speichern.'}
                </Drawer.Description>
              </Stack>
            </Drawer.Header>

            <Drawer.Body>
              <Stack gap={6}>
                <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                  <Field.Root required>
                    <Field.Label>
                      Leistungsempfänger
                      <Field.RequiredIndicator />
                    </Field.Label>

                    <Combobox.Root
                      openOnClick
                      collection={bankCollection}
                      value={customerId ? [customerId] : []}
                      inputValue={customerSearch}
                      onInputValueChange={details => setCustomerSearch(details.inputValue)}
                      onValueChange={details => {
                        const value = details.value[0] ?? ''

                        setCustomerId(value)
                        setCustomerSearch(details.items[0]?.label ?? '')
                      }}
                    >
                      <Combobox.Control>
                        <Combobox.Input
                          ref={bankInputRef}
                          placeholder={
                            isLoadingBanks
                              ? 'Banken werden geladen...'
                              : 'Bank auswählen oder tippen'
                          }
                        />
                        <Combobox.IndicatorGroup>
                          <Combobox.ClearTrigger
                            onClick={() => {
                              setCustomerId('')
                              setCustomerSearch('')
                            }}
                          />
                          <Combobox.Trigger />
                        </Combobox.IndicatorGroup>
                      </Combobox.Control>

                      <Portal>
                        <Combobox.Positioner style={{ zIndex: 10000 }} zIndex={10000}>
                          <Combobox.Content>
                            <Combobox.Empty>Keine Leistungsempfänger gefunden</Combobox.Empty>
                            {bankCollection.items.map(item => (
                              <Combobox.Item item={item} key={item.value}>
                                {item.label}
                                <Combobox.ItemIndicator />
                              </Combobox.Item>
                            ))}
                          </Combobox.Content>
                        </Combobox.Positioner>
                      </Portal>
                    </Combobox.Root>
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>
                      PLZ
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      value={postalCode}
                      onChange={event => setPostalCode(event.target.value)}
                    />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>
                      Ort
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input value={city} onChange={event => setCity(event.target.value)} />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>
                      Vxx
                      <Field.RequiredIndicator />
                    </Field.Label>

                    <Select.Root
                      collection={salesRegionCollection}
                      value={version ? [version] : []}
                      onValueChange={({ value }) => setVersion(value[0] ?? '')}
                    >
                      <Select.HiddenSelect />

                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Vxx auswählen" />
                        </Select.Trigger>

                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>

                      <Select.Positioner>
                        <Select.Content zIndex="popover">
                          {SALES_REGION_OPTIONS.map(option => (
                            <Select.Item key={option.value} item={option}>
                              {option.label}
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Field.Root>
                </Grid>

                <Separator />

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Field.Root>
                    <Field.Label>TOJ</Field.Label>
                    <Input
                      type="number"
                      min={0}
                      value={buckets.toj}
                      onChange={event => updateBucket('toj', event.target.value)}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Beratungsstunden</Field.Label>
                    <Input
                      type="number"
                      min={0}
                      value={buckets.consulting}
                      onChange={event => updateBucket('consulting', event.target.value)}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Trainingstage</Field.Label>
                    <Input
                      type="number"
                      min={0}
                      value={buckets.training}
                      onChange={event => updateBucket('training', event.target.value)}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Pilot</Field.Label>
                    <Input
                      type="number"
                      min={0}
                      value={buckets.pilot}
                      onChange={event => updateBucket('pilot', event.target.value)}
                    />
                  </Field.Root>
                </Grid>

                <Separator />

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Field.Root required>
                    <Field.Label>
                      Zeitraum von
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      type="date"
                      value={periodStart}
                      onChange={event => setPeriodStart(event.target.value)}
                    />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>
                      Zeitraum bis
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      type="date"
                      value={periodEnd}
                      onChange={event => setPeriodEnd(event.target.value)}
                    />
                  </Field.Root>
                </Grid>

                <Field.Root>
                  <Field.Label>Themen</Field.Label>

                  <Menu.Root closeOnSelect={false}>
                    <Menu.Trigger asChild>
                      <Button variant="outline" justifyContent="space-between" w="full">
                        {topics.length === 0
                          ? 'Themen auswählen'
                          : TOPIC_OPTIONS.filter(option => topics.includes(option.value))
                              .map(option => option.label)
                              .join(', ')}
                      </Button>
                    </Menu.Trigger>

                    <Menu.Positioner>
                      <Menu.Content zIndex="popover">
                        {TOPIC_OPTIONS.map(option => {
                          const checked = topics.includes(option.value)

                          return (
                            <Menu.Item
                              key={option.value}
                              value={option.value}
                              onClick={() => {
                                setTopics(prev =>
                                  checked
                                    ? prev.filter(value => value !== option.value)
                                    : [...prev, option.value]
                                )
                              }}
                            >
                              <Checkbox.Root checked={checked} pointerEvents="none">
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                                <Checkbox.Label>{option.label}</Checkbox.Label>
                              </Checkbox.Root>
                            </Menu.Item>
                          )
                        })}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                </Field.Root>
              </Stack>
            </Drawer.Body>

            <Drawer.Footer>
              <HStack>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Abbrechen
                </Button>

                <Button
                  colorPalette="gray"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  {demand ? 'Bedarf speichern' : 'Bedarf prüfen'}
                </Button>
              </HStack>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
